import React, { useState, useEffect, useCallback, useRef, lazy, Suspense, useMemo, ReactNode } from 'react'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import HistoryPanel from '../components/HistoryPanel'
import RTAuditHistory from '../components/RTAuditHistory'
import { errorMessage } from '../utils/friendlyError'

// Lazy-loaded instructions page
const RTAuditInstructions = lazy(() => import('./RTAuditInstructions'))

// ============================================================================
// Types
// ============================================================================

interface RTAuditJob {
  jobId: string
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  totalDealers: number
  completedDealers: number
  failedDealers: number
  progress: number
  statusMessage: string
  createdAt: number
  updatedAt?: number
  completedAt?: number
  summary?: {
    totalAudited: number
    passed: number
    failed: number
    errors: number
    passRate: number
    criteriaPassRates: Record<string, number>
    averageScore: number
  }
  driveUploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed'
  driveRootFolderUrl?: string
  driveFolderExists?: boolean | null
}

interface RTAuditDealerResult {
  dealerCode: string
  dealerUrl: string
  rentalPageUrl: string | null
  rentalPageUrls?: string[]
  c1_logo: string
  c2_link: string
  c3_phone: string
  c4_about: string
  c5_pricing: string
  c6_reservations: string
  c7_displayed: string
  subtotal: number
  totalPoints: number
  passStatus: 'Pass' | 'Fail'
  screenshotKeys: string[]
  screenshotUrls?: string[]
  /** True when this dealer's result was replayed from the 7-day capture cache. */
  cached?: boolean
  /** Epoch ms of the original cached capture (for the "cached · date" badge). */
  capturedAt?: number
  notes: string
  flags: string[]
  /**
   * Server-side capture/eval tags (e.g., SB/WU recovery markers) that
   * surface as humanized flags in the expand panel.
   */
  screenshotFlags?: string[]
  confidence: string
  /**
   * True when something about this dealer's evaluation needs a human glance
   * (unrecovered access failure, timeout, discarded capture, Error criteria,
   * or low confidence). Set server-side; older records derive it from flags.
   */
  needsReview?: boolean
  thirdPartyPartner?: string | null
  thirdPartyConfidence?: 'high' | 'medium' | 'low' | null
  unlinkedPage?: boolean
  rentalPageDiscovery?: string
}

/** Single predicate for the row badge + the Needs Review filter. */
function resultNeedsReview(r: { needsReview?: boolean; flags?: string[]; confidence?: string }): boolean {
  return r.needsReview === true || (r.flags?.length ?? 0) > 0 || (r.confidence || '').toLowerCase() === 'low'
}

interface ParsedCSVRow {
  dealerCode: string
  dealerUrl: string
  c7: string
}

interface CSVParseResult {
  rows: ParsedCSVRow[]
  warnings: string[]
  headers: string[]
}

type SortField = 'dealerCode' | 'dealerUrl' | 'subtotal' | 'passStatus' | 'c1_logo' | 'c2_link' | 'c3_phone' | 'c4_about' | 'c5_pricing' | 'c6_reservations' | 'c7_displayed'
type SortDirection = 'asc' | 'desc'
type ResultFilter = 'all' | 'pass' | 'fail' | 'flagged'

// ============================================================================
// Example CSV Template
// ============================================================================

/** Consecutive failed status polls (5 s apart, so about five minutes) before the page gives up. */
const MAX_POLL_FAILURES = 60

const EXAMPLE_CSV = `DEALER CODE,DEALER URL,C7
10098,https://www.chathamparkwaytoyota.com,Y
12015,https://www.continentaltoyota.com,Y
19063,https://www.darcarstoyotaoffrederick.com,N
2044,https://www.righttoyota.com,Y
22048,https://www.walsertoyota.com,Y
25060,https://www.toyotaofbozeman.com,No Audit
29073,https://www.teamtoyotaprinceton.com,Y
31024,https://www.kinderhooktoyota.com,N
32069,https://www.petersontoyota.com,Y
34133,https://www.toyotaofkent.com,No Audit
`

// ============================================================================
// CSV Parsing
// ============================================================================

function parseCSV(text: string): CSVParseResult {
  const warnings: string[] = []

  // Strip BOM
  const cleaned = text.replace(/^\uFEFF/, '')

  // Split by newline
  const lines = cleaned.split(/\r?\n/).filter(line => line.trim())

  if (lines.length < 2) {
    return { rows: [], warnings: ['CSV file appears to be empty or has no data rows.'], headers: [] }
  }

  // Parse headers (case-insensitive matching)
  const headerLine = lines[0]
  const headers = headerLine.split(',').map(h => h.trim().replace(/^["']|["']$/g, ''))
  const headerLower = headers.map(h => h.toLowerCase().replace(/[\s_-]+/g, ''))

  // Find column indices
  const dealerCodeIdx = headerLower.findIndex(h => h === 'dealercode' || h === 'dealer_code' || h === 'code')
  const dealerUrlIdx = headerLower.findIndex(h => h === 'dealerurl' || h === 'dealer_url' || h === 'url' || h === 'website')
  const c7Idx = headerLower.findIndex(h => h === 'c7' || h === 'c7displayed' || h === 'c7_displayed')

  if (dealerCodeIdx === -1) {
    warnings.push('Missing required column: DEALER CODE (or dealer_code)')
  }
  if (dealerUrlIdx === -1) {
    warnings.push('Missing required column: DEALER URL (or dealer_url)')
  }

  const rows: ParsedCSVRow[] = []
  const urlPattern = /^https?:\/\/.+/i

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i])

    const dealerCode = dealerCodeIdx >= 0 ? (cols[dealerCodeIdx] || '').trim() : ''
    const dealerUrl = dealerUrlIdx >= 0 ? (cols[dealerUrlIdx] || '').trim() : ''
    const c7 = c7Idx >= 0 ? (cols[c7Idx] || '').trim().toUpperCase() : ''

    if (!dealerCode && !dealerUrl) continue

    if (dealerUrl && !urlPattern.test(dealerUrl)) {
      warnings.push(`Row ${i + 1}: Malformed URL "${dealerUrl}" for dealer ${dealerCode || '(unknown)'}`)
    }

    rows.push({ dealerCode, dealerUrl, c7 })
  }

  if (rows.length === 0 && warnings.length === 0) {
    warnings.push('No valid data rows found in CSV.')
  }

  return { rows, warnings, headers }
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"'
        i++
      } else if (char === '"') {
        inQuotes = false
      } else {
        current += char
      }
    } else {
      if (char === '"') {
        inQuotes = true
      } else if (char === ',') {
        result.push(current)
        current = ''
      } else {
        current += char
      }
    }
  }
  result.push(current)
  return result
}

// ============================================================================
// Helper Components
// ============================================================================

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className="toast toast-success fixed bottom-4 right-4 animate-fade-in z-50">
      <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-linear-text">{message}</span>
    </div>
  )
}

function ConfirmModal({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading,
  danger,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
  danger?: boolean
}) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal w-full max-w-md animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="text-[15px] font-medium text-linear-text">{title}</h3>
          <button onClick={onCancel} className="text-linear-text-muted hover:text-linear-text transition-colors" aria-label="Close">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4l8 8M4 12l8-8" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="text-[13px] text-linear-text-muted">{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel} disabled={isLoading}>Cancel</button>
          <button
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && <span className="spinner spinner-sm" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function CriterionCell({ value }: { value: string }) {
  const upper = (value || '').toUpperCase().trim()
  if (upper === 'Y') return <span className="text-success font-medium">Y</span>
  if (upper === 'N') return <span className="text-error font-medium">N</span>
  if (upper === 'ERROR') return <span className="text-warning font-medium">Error</span>
  if (upper === 'NO AUDIT') return <span className="text-linear-text-placeholder">No Audit</span>
  return <span className="text-linear-text-muted">{value || '-'}</span>
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  return (
    <svg className={`w-3 h-3 inline-block ml-1 ${active ? 'text-linear-accent' : 'text-linear-text-placeholder'}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
      {direction === 'asc' ? (
        <path d="M6 2v8M3 5l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M6 2v8M3 7l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  )
}

// ============================================================================
// Main Component
// ============================================================================

function RTAuditMain({ onViewChange, onHeaderActionsChange }: { onViewChange?: (view: 'home' | 'processing' | 'results' | 'error') => void; onHeaderActionsChange?: (actions: ReactNode) => void }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const jobId = searchParams.get('jobId')

  // Upload state
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [parsedCSV, setParsedCSV] = useState<CSVParseResult | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Capture-cache clear (pre-run)
  const [showClearCache, setShowClearCache] = useState(false)
  const [isClearingCache, setIsClearingCache] = useState(false)
  const [clearCacheMsg, setClearCacheMsg] = useState<string | null>(null)
  // Pre-run cache status for the uploaded CSV's dealers (cached vs fresh).
  const [cacheStatus, setCacheStatus] = useState<{ cachedCount: number; total: number; ttlDays: number } | null>(null)
  // Global count of dealers with a cached capture (landing callout + Clear Cache enable).
  const [globalCachedCount, setGlobalCachedCount] = useState<number | null>(null)

  // Job state
  const [jobStatus, setJobStatus] = useState<RTAuditJob | null>(null)
  const [results, setResults] = useState<RTAuditDealerResult[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  // Runs cost real money (Bright Data per-capture), so starting takes a
  // second confirmation.
  const [showStartConfirm, setShowStartConfirm] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isRetryingUpload, setIsRetryingUpload] = useState(false)

  // Results view state
  const [sortField, setSortField] = useState<SortField>('dealerCode')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  // History panel state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)
  const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0)

  // UI state
  const [toast, setToast] = useState<string | null>(null)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ jobId: string; ownerName?: string } | null>(null)
  const [lastDeletedJobId, setLastDeletedJobId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Criteria tooltip state
  const [openTooltip, setOpenTooltip] = useState<string | null>(null)

  // Animate pass rate bars on mount
  const [barsAnimated, setBarsAnimated] = useState(false)

  // Image modal state
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean
    currentIndex: number
    images: Array<{
      url: string
      dealerCode: string
      label: string
    }>
    zoomLevel: number
  }>({
    isOpen: false,
    currentIndex: 0,
    images: [],
    zoomLevel: 1,
  })

  // Polling ref
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Consecutive failed status polls. A poll that fails once (expired token,
  // network blip, 5xx) no longer stops polling, which froze the progress
  // screen until a refresh; polling stops only on a lost job or a long outage.
  const pollFailuresRef = useRef(0)
  const [pollWarning, setPollWarning] = useState<string | null>(null)
  const resultsLoadedRef = useRef(false)


  // -----------------------------------------------------------------------
  // Polling logic
  // -----------------------------------------------------------------------

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  // Image modal handlers
  const openImageModal = (
    url: string,
    dealerCode: string,
    label: string,
    allImages?: Array<{ url: string; dealerCode: string; label: string }>
  ) => {
    const images = allImages || [{ url, dealerCode, label }]
    const currentIndex = images.findIndex((img) => img.url === url)
    setImageModal({
      isOpen: true,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      images,
      zoomLevel: 1,
    })
  }

  const closeImageModal = () => {
    setImageModal({ isOpen: false, currentIndex: 0, images: [], zoomLevel: 1 })
  }

  const navigateImage = (direction: 'prev' | 'next') => {
    setImageModal((prev) => {
      const newIndex = direction === 'prev'
        ? Math.max(0, prev.currentIndex - 1)
        : Math.min(prev.images.length - 1, prev.currentIndex + 1)
      return { ...prev, currentIndex: newIndex, zoomLevel: 1 }
    })
  }

  const handleZoom = (delta: number) => {
    setImageModal((prev) => ({
      ...prev,
      zoomLevel: Math.max(0.5, Math.min(3, prev.zoomLevel + delta)),
    }))
  }

  const loadResults = useCallback(async (id: string) => {
    try {
      const { getRTAuditResults } = await import('../api/index')
      const res = await getRTAuditResults(id)
      setResults(res.results)
    } catch (err) {
      setError(errorMessage(err) || 'Failed to load results')
    }
  }, [])

  const pollStatus = useCallback(async (id: string) => {
    try {
      const { getRTAuditStatus } = await import('../api/index')
      const status = await getRTAuditStatus(id)
      pollFailuresRef.current = 0
      setPollWarning(null)
      setJobStatus(status)

      if (status.status === 'completed') {
        const isDriveActive = status.driveUploadStatus === 'pending' || status.driveUploadStatus === 'uploading'
        if (!isDriveActive) {
          stopPolling()
        }
        if (!resultsLoadedRef.current) {
          resultsLoadedRef.current = true
          await loadResults(id)
        }
        setHistoryRefreshTrigger((c) => c + 1)
      } else if (status.status === 'failed' || status.status === 'cancelled') {
        stopPolling()
        setHistoryRefreshTrigger((c) => c + 1)
        if (status.status === 'failed') {
          setError(status.statusMessage || 'Audit failed')
        }
      }
    } catch (err) {
      const httpStatus = (err as { status?: number }).status
      pollFailuresRef.current += 1
      if (httpStatus === 403 || httpStatus === 404 || pollFailuresRef.current >= MAX_POLL_FAILURES) {
        setPollWarning(null)
        setError(errorMessage(err) || 'Failed to get job status')
        stopPolling()
        return
      }
      setPollWarning('Lost contact with the audit status for a moment. Retrying…')
    }
  }, [stopPolling, loadResults])

  // -----------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------

  // Load job on mount if jobId in URL
  // Reset state when jobId changes (navigating between jobs or back to upload view)
  const prevJobIdRef = useRef(jobId)
  useEffect(() => {
    if (jobId !== prevJobIdRef.current) {
      // Always reset when switching jobs
      setJobStatus(null)
      setResults([])
      setError(null)
      setExpandedRow(null)
      setBarsAnimated(false)
      resultsLoadedRef.current = false
      stopPolling()
      if (!jobId) {
        setCsvFile(null)
        setParsedCSV(null)
      }
      prevJobIdRef.current = jobId
    }
  }, [jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-restore running job on page load (when user navigates away and back)
  useEffect(() => {
    if (jobId) return // Already viewing a job
    let cancelled = false
    ;(async () => {
      try {
        const { getRTAuditHistory } = await import('../api/index')
        const { jobs } = await getRTAuditHistory('mine')
        if (cancelled) return
        const activeJob = jobs.find(
          (j) => j.status === 'processing' || j.status === 'pending'
        )
        if (activeJob) {
          setSearchParams({ jobId: activeJob.jobId })
        }
      } catch {
        // Silently ignore — user will see the normal upload view
      }
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (jobId) {
      pollStatus(jobId).then(() => {
        // After first check, start polling if still processing
      })
    }
    return () => stopPolling()
  }, [jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Start polling when job is processing or Drive upload is in progress
  useEffect(() => {
    const isProcessing = jobStatus?.status === 'processing' || jobStatus?.status === 'pending'
    const isDriveUploading = jobStatus?.status === 'completed' && (jobStatus?.driveUploadStatus === 'pending' || jobStatus?.driveUploadStatus === 'uploading')
    if (jobId && jobStatus && (isProcessing || isDriveUploading)) {
      const interval = setInterval(() => {
        pollStatus(jobId)
      }, 5000)
      pollIntervalRef.current = interval as unknown as ReturnType<typeof setInterval>
      return () => clearInterval(interval)
    }
  }, [jobId, jobStatus?.status, jobStatus?.driveUploadStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load results when status becomes completed
  useEffect(() => {
    if (jobId && jobStatus?.status === 'completed' && results.length === 0) {
      loadResults(jobId)
    }
  }, [jobId, jobStatus?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Landing: total dealers with a cached capture (callout + Clear Cache enable).
  const refreshGlobalCacheCount = useCallback(async () => {
    try {
      const { getRTAuditCacheSummary } = await import('../api/index')
      const res = await getRTAuditCacheSummary()
      setGlobalCachedCount(res.totalCached)
    } catch {
      setGlobalCachedCount(null)
    }
  }, [])
  useEffect(() => { refreshGlobalCacheCount() }, [refreshGlobalCacheCount])

  // Pre-run: how many of the uploaded CSV's dealers already have a fresh cached
  // capture (so the user knows what will be reused vs captured fresh).
  useEffect(() => {
    let cancelled = false
    const rows = parsedCSV?.rows ?? []
    if (rows.length === 0) { setCacheStatus(null); return }
    const dealers = rows
      .filter(r => r.dealerCode && r.dealerUrl)
      .map(r => ({ dealerCode: r.dealerCode, dealerUrl: r.dealerUrl }))
    if (dealers.length === 0) { setCacheStatus(null); return }
    ;(async () => {
      try {
        const { getRTAuditCacheStatus } = await import('../api/index')
        const res = await getRTAuditCacheStatus(dealers)
        if (!cancelled) setCacheStatus({ cachedCount: res.cachedCount, total: dealers.length, ttlDays: res.ttlDays })
      } catch {
        if (!cancelled) setCacheStatus(null)
      }
    })()
    return () => { cancelled = true }
  }, [parsedCSV])

  // Trigger pass rate bar animation after results load
  useEffect(() => {
    if (results.length > 0 && !barsAnimated) {
      requestAnimationFrame(() => setBarsAnimated(true))
    }
  }, [results.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  // Notify parent of current view for breadcrumb updates
  useEffect(() => {
    if (!onViewChange) return
    if (jobId && jobStatus && (jobStatus.status === 'processing' || jobStatus.status === 'pending')) onViewChange('processing')
    else if (jobId && jobStatus && (jobStatus.status === 'failed' || jobStatus.status === 'cancelled')) onViewChange('error')
    else if (jobId && jobStatus?.status === 'completed') onViewChange('results')
    else onViewChange('home')
  }, [jobId, jobStatus?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Push header actions (history icon) up to parent AppLayout
  useEffect(() => {
    if (!onHeaderActionsChange) return
    onHeaderActionsChange(
      <div className="flex items-center gap-1">
        <button
          onClick={() => setShowHistoryPanel(prev => !prev)}
          className={`p-2 rounded-lg transition-colors ${
            showHistoryPanel
              ? 'text-linear-accent bg-linear-accent/10'
              : 'text-linear-text-secondary hover:text-linear-text hover:bg-linear-bg-hover'
          }`}
          title="Audit History"
          aria-label="Audit History"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>
    )
  }, [showHistoryPanel, onHeaderActionsChange])

  // Close criteria tooltip on outside click
  useEffect(() => {
    if (!openTooltip) return
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-criteria-tooltip]')) {
        setOpenTooltip(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [openTooltip])

  // Keyboard controls for image modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!imageModal.isOpen) return
      switch (e.key) {
        case 'Escape': closeImageModal(); break
        case 'ArrowLeft': navigateImage('prev'); break
        case 'ArrowRight': navigateImage('next'); break
        case '+': case '=': handleZoom(0.25); break
        case '-': case '_': handleZoom(-0.25); break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [imageModal.isOpen, imageModal.currentIndex, imageModal.images.length])

  // -----------------------------------------------------------------------
  // CSV Upload handlers
  // -----------------------------------------------------------------------

  const handleFileSelect = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setToast('Please upload a .csv file')
      return
    }
    setCsvFile(file)
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      if (text) {
        const result = parseCSV(text)
        setParsedCSV(result)
      }
    }
    reader.readAsText(file)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleDownloadExample = () => {
    const blob = new Blob([EXAMPLE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'rt-audit-example-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleStartAudit = async () => {
    if (!csvFile || !parsedCSV || parsedCSV.rows.length === 0 || isStarting) return
    setIsStarting(true)
    setError(null)

    try {
      const { startRTAudit } = await import('../api/index')

      // Convert CSV file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result as string
          resolve(result.split(',')[1])
        }
        reader.onerror = reject
        reader.readAsDataURL(csvFile)
      })

      const response = await startRTAudit({ csvData: base64, fileName: csvFile.name })
      setSearchParams({ jobId: response.jobId })
      setJobStatus({
        jobId: response.jobId,
        status: 'pending',
        totalDealers: parsedCSV.rows.length,
        completedDealers: 0,
        failedDealers: 0,
        progress: 0,
        statusMessage: 'Starting audit...',
        createdAt: Date.now(),
      })
      setToast('Audit started successfully')
      setHistoryRefreshTrigger((c) => c + 1)
    } catch (err) {
      // startRTAudit attaches activeJobId to the Error on a 409
      const activeJobId = err instanceof Error ? (err as Error & { activeJobId?: string }).activeJobId : undefined
      if (activeJobId) {
        // User already has a running audit — redirect to it
        setSearchParams({ jobId: activeJobId })
        setToast('Redirected to your running audit')
      } else {
        setError(errorMessage(err) || 'Failed to start audit')
      }
    } finally {
      setIsStarting(false)
    }
  }

  const handleCancelAudit = async () => {
    if (!jobId) return
    setIsCancelling(true)
    try {
      const { cancelRTAudit } = await import('../api/index')
      await cancelRTAudit(jobId)
      setToast('Audit cancelled')
      stopPolling()
      setHistoryRefreshTrigger((c) => c + 1)
      // Leave the cancelled job behind entirely. Keeping ?jobId= in the URL
      // meant a refresh re-attached to the dead job (and, before the
      // coordinator guard, could surface it as running again).
      setSearchParams({})
    } catch (err) {
      setToast('Failed to cancel: ' + (errorMessage(err) || 'Unknown error'))
    } finally {
      setIsCancelling(false)
      setShowCancelConfirm(false)
    }
  }

  const handleExport = async () => {
    if (!jobId) return
    setIsExporting(true)
    try {
      const { exportRTAudit } = await import('../api/index')
      const { blob, fileName } = await exportRTAudit(jobId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setToast('Export downloaded')
    } catch (err) {
      setToast('Export failed: ' + (errorMessage(err) || 'Unknown error'))
    } finally {
      setIsExporting(false)
    }
  }


  const handleNewAudit = () => {
    setSearchParams({})
    setJobStatus(null)
    setResults([])
    setCsvFile(null)
    setParsedCSV(null)
    setError(null)
    setExpandedRow(null)
    stopPolling()
    navigate('/rt-audit/')
  }

  const handleClearCache = async () => {
    setIsClearingCache(true)
    setClearCacheMsg(null)
    try {
      const { clearRTAuditCache } = await import('../api/index')
      const res = await clearRTAuditCache()
      setClearCacheMsg(`Cache cleared, ${res.objectsDeleted} cached item${res.objectsDeleted === 1 ? '' : 's'} removed. The next run captures everything fresh.`)
      setShowClearCache(false)
      setGlobalCachedCount(0)
      setCacheStatus(null)
    } catch (err) {
      setClearCacheMsg(errorMessage(err) || 'Failed to clear the cache.')
      setShowClearCache(false)
    } finally {
      setIsClearingCache(false)
    }
  }

  const handleDeleteAudit = async (id: string) => {
    setIsDeleting(true)
    try {
      const { deleteRTAudit } = await import('../api/index')
      await deleteRTAudit(id)
      setLastDeletedJobId(id)
      setToast('Audit deleted')

      // If we're viewing this audit's results, go back to home
      if (jobId === id) {
        setJobStatus(null)
        setResults([])
        setError(null)
        navigate('/rt-audit/')
      }
    } catch (err) {
      setToast('Delete failed: ' + (errorMessage(err) || 'Unknown error'))
    } finally {
      setIsDeleting(false)
      setDeleteConfirm(null)
    }
  }

  const handleViewHistoryJob = (id: string) => {
    setShowHistoryPanel(false)
    navigate('/rt-audit/?jobId=' + id)
  }

  // -----------------------------------------------------------------------
  // Computed data for results
  // -----------------------------------------------------------------------

  const csvSummary = useMemo(() => {
    if (!parsedCSV) return null
    const total = parsedCSV.rows.length
    const c7Y = parsedCSV.rows.filter(r => r.c7 === 'Y').length
    const c7N = parsedCSV.rows.filter(r => r.c7 === 'N').length
    const c7NoAudit = parsedCSV.rows.filter(r => r.c7 === 'NO AUDIT' || r.c7 === 'NOAUDIT').length
    return { total, c7Y, c7N, c7NoAudit }
  }, [parsedCSV])

  const filteredResults = useMemo(() => {
    let filtered = [...results]

    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(r =>
        r.dealerCode.toLowerCase().includes(q) || r.dealerUrl.toLowerCase().includes(q)
      )
    }

    // Apply result filter
    if (resultFilter === 'pass') {
      filtered = filtered.filter(r => r.passStatus === 'Pass')
    } else if (resultFilter === 'fail') {
      filtered = filtered.filter(r => r.passStatus === 'Fail')
    } else if (resultFilter === 'flagged') {
      filtered = filtered.filter(resultNeedsReview)
    }

    // Apply sort
    filtered.sort((a, b) => {
      let aVal: string | number = ''
      let bVal: string | number = ''

      switch (sortField) {
        case 'dealerCode': aVal = a.dealerCode; bVal = b.dealerCode; break
        case 'dealerUrl': aVal = a.dealerUrl; bVal = b.dealerUrl; break
        case 'subtotal': aVal = a.subtotal; bVal = b.subtotal; break
        case 'passStatus': aVal = a.passStatus; bVal = b.passStatus; break
        case 'c1_logo': aVal = a.c1_logo; bVal = b.c1_logo; break
        case 'c2_link': aVal = a.c2_link; bVal = b.c2_link; break
        case 'c3_phone': aVal = a.c3_phone; bVal = b.c3_phone; break
        case 'c4_about': aVal = a.c4_about; bVal = b.c4_about; break
        case 'c5_pricing': aVal = a.c5_pricing; bVal = b.c5_pricing; break
        case 'c6_reservations': aVal = a.c6_reservations; bVal = b.c6_reservations; break
        case 'c7_displayed': aVal = a.c7_displayed; bVal = b.c7_displayed; break
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      const cmp = String(aVal).localeCompare(String(bVal))
      return sortDirection === 'asc' ? cmp : -cmp
    })

    return filtered
  }, [results, searchQuery, resultFilter, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // -----------------------------------------------------------------------
  // Shared overlays (rendered in every view)
  // -----------------------------------------------------------------------

  const renderOverlays = () => (
    <>
      {showHistoryPanel && (
        <HistoryPanel title="Audit History" onClose={() => setShowHistoryPanel(false)}>
          <RTAuditHistory
            onDeleteRequest={(id, ownerName) => setDeleteConfirm({ jobId: id, ownerName })}
            onResumeRequest={async (id) => {
              try {
                const { resumeRTAudit } = await import('../api/index')
                await resumeRTAudit(id)
                setShowHistoryPanel(false)
                setError(null)
                setSearchParams({ jobId: id })
                pollStatus(id)
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to resume audit')
              }
            }}
            onSelectJob={(id) => handleViewHistoryJob(id)}
            embedded
            deletedJobId={lastDeletedJobId}
            refreshTrigger={historyRefreshTrigger}
          />
        </HistoryPanel>
      )}
      {deleteConfirm && (
        <ConfirmModal
          title="Delete Audit"
          message={
            deleteConfirm.ownerName
              ? `This audit belongs to ${deleteConfirm.ownerName}. Are you sure you want to delete it? This removes all data, screenshots, and the Google Drive folder. This action cannot be undone.`
              : 'Are you sure you want to delete this audit? This removes all data, screenshots, and the Google Drive folder. This action cannot be undone.'
          }
          confirmLabel="Delete"
          onConfirm={() => handleDeleteAudit(deleteConfirm.jobId)}
          onCancel={() => setDeleteConfirm(null)}
          isLoading={isDeleting}
          danger
        />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </>
  )

  // -----------------------------------------------------------------------
  // Render: Loading state (jobId in URL but status not yet fetched)
  // -----------------------------------------------------------------------

  if (jobId && !jobStatus) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="card text-center py-16">
          {error ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-error/10">
                <svg className="w-8 h-8 text-error" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 10v6M16 20v1M8 26h16l-8-18L8 26z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="text-[17px] font-medium text-linear-text mb-2">Failed to load audit</h2>
              <p className="text-[13px] text-linear-text-muted mb-6 max-w-md mx-auto">{error}</p>
              <div className="flex items-center justify-center gap-3">
                <button className="btn btn-primary" onClick={() => { setError(null); pollStatus(jobId) }}>
                  Retry
                </button>
                <button className="btn btn-outline" onClick={handleNewAudit}>
                  New Audit
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 border-2 border-linear-accent/30 border-t-linear-accent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[13px] text-linear-text-muted">Loading audit...</p>
            </>
          )}
        </div>
        {renderOverlays()}
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render: Error/Failed state
  // -----------------------------------------------------------------------

  if (jobId && jobStatus && (jobStatus.status === 'failed' || jobStatus.status === 'cancelled')) {
    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="card text-center py-16">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            jobStatus.status === 'failed' ? 'bg-error/10' : 'bg-warning/10'
          }`}>
            <svg className={`w-8 h-8 ${jobStatus.status === 'failed' ? 'text-error' : 'text-warning'}`} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
              {jobStatus.status === 'failed' ? (
                <path d="M16 10v6M16 20v1M8 26h16l-8-18L8 26z" strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <path d="M10 10l12 12M10 22l12-12" strokeLinecap="round" />
              )}
            </svg>
          </div>
          <h2 className="text-[17px] font-medium text-linear-text mb-2">
            {jobStatus.status === 'failed' ? 'Audit Failed' : 'Audit Cancelled'}
          </h2>
          <p className="text-[13px] text-linear-text-muted mb-6 max-w-md mx-auto">
            {error || jobStatus.statusMessage || (jobStatus.status === 'failed' ? 'An error occurred while processing the audit.' : 'This audit was cancelled.')}
          </p>
          <div className="flex items-center justify-center gap-3">
            {/* Resume re-runs dealers with error rows, so offer it only when there are some
                (an audit that failed before dispatch has none). */}
            {(jobStatus.status === 'failed' || jobStatus.status === 'cancelled') && jobStatus.failedDealers > 0 && (
              <button className="btn btn-primary" onClick={async () => {
                try {
                  const { resumeRTAudit } = await import('../api/index')
                  await resumeRTAudit(jobId!)
                  setError(null)
                  pollStatus(jobId!)
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to resume audit')
                }
              }}>
                Resume Failed ({jobStatus.failedDealers} dealer{jobStatus.failedDealers !== 1 ? 's' : ''})
              </button>
            )}
            <button className="btn btn-outline" onClick={handleNewAudit}>
              New Audit
            </button>
          </div>
        </div>
        {renderOverlays()}
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render: Processing state
  // -----------------------------------------------------------------------

  if (jobId && jobStatus && (jobStatus.status === 'processing' || jobStatus.status === 'pending')) {
    const progressPercent = Math.min(100, jobStatus.progress || 0)

    // User-friendly status message: skip internal coordinator/worker messages
    const isWarmingUp = jobStatus.status === 'processing' && jobStatus.completedDealers === 0
    const friendlyMessage = (() => {
      const msg = jobStatus.statusMessage || ''
      if (jobStatus.completedDealers > 0) {
        return `Auditing dealer websites: ${jobStatus.completedDealers} of ${jobStatus.totalDealers} completed`
      }
      if (msg.includes('dispatched') || msg.includes('Coordinator') || jobStatus.status === 'pending') {
        return 'Preparing to audit dealer websites...'
      }
      if (isWarmingUp) {
        return 'Warming up workers, results coming soon...'
      }
      return 'Auditing dealer websites...'
    })()

    return (
      <div className="max-w-[1400px] mx-auto">
        <div className="card max-w-xl mx-auto text-center py-12">
          <div className="spinner spinner-lg mx-auto mb-6" />
          <h2 className="text-[17px] font-medium text-linear-text mb-2">
            Processing Audit
          </h2>
          <p className="text-[13px] text-linear-text-muted mb-6">
            {friendlyMessage}
          </p>

          {/* Progress bar */}
          <div className="mb-4 px-8">
            <div className={`progress-bar ${isWarmingUp ? 'progress-bar-indeterminate' : ''}`}>
              {!isWarmingUp && (
                <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
              )}
            </div>
          </div>

          <div className="flex items-center justify-center gap-6 text-[13px] text-linear-text-muted mb-6">
            <span>
              <span className="text-linear-text font-medium">{jobStatus.completedDealers}</span>
              {' / '}
              <span>{jobStatus.totalDealers}</span>
              {' dealers'}
            </span>
            <span className="text-linear-text font-medium">{progressPercent}%</span>
          </div>

          {jobStatus.failedDealers > 0 && (
            <p className="text-[12px] text-warning mb-4">
              {jobStatus.failedDealers} dealer(s) encountered errors
            </p>
          )}

          {pollWarning && (
            <p className="text-[12px] text-linear-text-muted mb-4" role="status" data-testid="rt-poll-warning">
              {pollWarning}
            </p>
          )}

          <button
            className="btn btn-outline"
            onClick={() => setShowCancelConfirm(true)}
            disabled={isCancelling}
          >
            Cancel Audit
          </button>
        </div>

        {showCancelConfirm && (
          <ConfirmModal
            title="Cancel Audit"
            message="Are you sure you want to cancel this audit? Progress will be lost."
            confirmLabel="Cancel Audit"
            onConfirm={handleCancelAudit}
            onCancel={() => setShowCancelConfirm(false)}
            isLoading={isCancelling}
            danger
          />
        )}

        {renderOverlays()}
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render: Results state
  // -----------------------------------------------------------------------

  if (jobId && jobStatus?.status === 'completed') {
    const criteriaLabels: Record<string, string> = {
      c1_logo: 'C1 - Logo',
      c2_link: 'C2 - Link',
      c3_phone: 'C3 - Phone',
      c4_about: 'C4 - About',
      c5_pricing: 'C5 - Pricing',
      c6_reservations: 'C6 - Reservations',
      c7_displayed: 'C7 - Displayed',
    }

    // Use server summary if available, otherwise compute from results
    const summary = jobStatus.summary ?? (results.length > 0 ? (() => {
      const totalAudited = results.length
      const passed = results.filter(r => r.passStatus === 'Pass').length
      const failed = results.filter(r => r.passStatus === 'Fail').length
      const errors = results.filter(r => r.notes?.includes('Error') || r.flags?.some(f => f.includes('error') || f.includes('timeout'))).length
      const passRate = totalAudited > 0 ? (passed / totalAudited) * 100 : 0
      // 2026-05-11 — was r.totalPoints (the max possible = 12 or 14),
      // which made the average equal the max when summary wasn't yet
      // returned by the backend. r.subtotal is the dealer's actual
      // score out of totalPoints.
      const averageScore = totalAudited > 0 ? results.reduce((sum, r) => sum + (r.subtotal || 0), 0) / totalAudited : 0
      const criteriaKeys = ['c1_logo', 'c2_link', 'c3_phone', 'c4_about', 'c5_pricing', 'c6_reservations'] as const
      const criteriaPassRates: Record<string, number> = {}
      for (const key of criteriaKeys) {
        const eligible = results.filter(r => r[key] !== 'No Audit' && r[key] !== 'Error')
        criteriaPassRates[key] = eligible.length > 0
          ? Math.round((eligible.filter(r => r[key] === 'Y').length / eligible.length) * 100)
          : 0
      }
      return { totalAudited, passed, failed, errors, passRate, averageScore, criteriaPassRates }
    })() : null)

    const isLoading = results.length === 0 || !summary

    return (
      <div className="max-w-[1400px] mx-auto">
        {/* Summary card */}
        <div className="card mb-6">
          {isLoading ? (
            /* Skeleton for summary card */
            <>
              {/* Timestamp skeleton */}
              <div className="h-3 w-48 bg-linear-bg-hover rounded animate-pulse mb-4" />
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-shrink-0 lg:pr-6 lg:border-r lg:border-linear-border">
                  <div className="h-10 w-24 bg-linear-bg-hover rounded animate-pulse" />
                  <div className="h-3 w-16 bg-linear-bg-hover rounded animate-pulse mt-2" />
                </div>
                <div className="flex flex-wrap gap-6 flex-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i}>
                      <div className="h-6 w-12 bg-linear-bg-hover rounded animate-pulse" />
                      <div className="h-3 w-16 bg-linear-bg-hover rounded animate-pulse mt-2" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-linear-border">
                <div className="h-3 w-40 bg-linear-bg-hover rounded animate-pulse mb-4" />
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-3 w-[120px] bg-linear-bg-hover rounded animate-pulse flex-shrink-0" />
                      <div className="flex-1 h-[6px] bg-linear-border rounded-full" />
                      <div className="h-3 w-[40px] bg-linear-bg-hover rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Real summary content */
            <>
              {/* Audit timestamp */}
              {jobStatus.createdAt && (
                <div className="flex items-center gap-1.5 mb-4 text-[12px] text-linear-text-muted">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span
                    className="tooltip-wrapper"
                    data-tooltip={(() => {
                      const fmt = (ts: number) => {
                        const d = new Date(ts)
                        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                        const tz = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || ''
                        return `${dateStr}, ${timeStr} ${tz}`
                      }
                      const parts = [`Started: ${fmt(jobStatus.createdAt)}`]
                      if (jobStatus.completedAt) parts.push(`Completed: ${fmt(jobStatus.completedAt)}`)
                      return parts.join(' · ')
                    })()}
                  >
                    {(() => {
                      const ts = jobStatus.completedAt || jobStatus.createdAt
                      const date = new Date(ts)
                      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      const tz = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || ''
                      return `${dateStr}, ${timeStr} ${tz}`
                    })()}
                  </span>
                </div>
              )}
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Pass rate */}
                <div className="flex-shrink-0 text-center lg:text-left lg:pr-6 lg:border-r lg:border-linear-border">
                  <div className={`text-[40px] font-bold leading-tight ${
                    summary.passRate >= 80 ? 'text-success' :
                    summary.passRate >= 50 ? 'text-warning' : 'text-error'
                  }`}>
                    {summary.passRate.toFixed(1)}%
                  </div>
                  <p className="text-[13px] text-linear-text-muted mt-1">Pass Rate</p>
                </div>

                {/* Stats */}
                <div className="flex flex-wrap gap-6 flex-1">
                  <div>
                    <div className="text-[20px] font-medium text-linear-text">{summary.totalAudited}</div>
                    <p className="text-[12px] text-linear-text-muted">Total Audited</p>
                  </div>
                  <div>
                    <div className="text-[20px] font-medium text-success">{summary.passed}</div>
                    <p className="text-[12px] text-linear-text-muted">Passed</p>
                  </div>
                  <div>
                    <div className="text-[20px] font-medium text-error">{summary.failed}</div>
                    <p className="text-[12px] text-linear-text-muted">Failed</p>
                  </div>
                  <div>
                    <div className="text-[20px] font-medium text-warning">{summary.errors}</div>
                    <p className="text-[12px] text-linear-text-muted">Errors</p>
                  </div>
                  {summary.averageScore !== undefined && (
                    <div>
                      <div className="text-[20px] font-medium text-linear-text">{summary.averageScore.toFixed(1)}</div>
                      <p className="text-[12px] text-linear-text-muted">Avg Score</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Criteria pass rates bar chart */}
              {summary.criteriaPassRates && Object.keys(summary.criteriaPassRates).length > 0 && (
                <div className="mt-6 pt-6 border-t border-linear-border">
                  <h4 className="text-[13px] font-medium text-linear-text-muted mb-4">Per-Criteria Pass Rates</h4>
                  <div className="space-y-3">
                    {Object.entries(summary.criteriaPassRates)
                    .filter(([key, rate]) => {
                      if (rate === 0) {
                        const allNoAudit = results.every(r => r[key as keyof RTAuditDealerResult] === 'No Audit')
                        if (allNoAudit) return false
                      }
                      return true
                    })
                    .sort(([a], [b]) => {
                      const numA = parseInt(a.replace(/\D/g, ''), 10)
                      const numB = parseInt(b.replace(/\D/g, ''), 10)
                      return numA - numB
                    }).map(([key, rate]) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="text-[12px] text-linear-text-muted w-[120px] flex-shrink-0 truncate">
                          {criteriaLabels[key] || key}
                        </span>
                        <div className="flex-1 h-[6px] bg-linear-border rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ease-out ${
                              rate >= 80 ? 'bg-success' :
                              rate >= 50 ? 'bg-warning' : 'bg-error'
                            }`}
                            style={{ width: barsAnimated ? `${Math.min(100, rate)}%` : '0%' }}
                          />
                        </div>
                        <span className={`text-[12px] font-medium w-[40px] text-right ${
                          rate >= 80 ? 'text-success' :
                          rate >= 50 ? 'text-warning' : 'text-error'
                        }`}>
                          {rate.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Controls row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-3">
          {/* Result filters */}
          <div className="segmented-control">
            {(['all', 'pass', 'fail', 'flagged'] as ResultFilter[]).map(f => (
              <button
                key={f}
                className={`segmented-control-option ${resultFilter === f ? 'active' : ''}`}
                onClick={() => setResultFilter(f)}
              >
                {isLoading
                  ? (f === 'all' ? 'All' : f === 'pass' ? 'Pass' : f === 'fail' ? 'Fail' : 'Needs Review')
                  : f === 'all' ? `All (${results.length})`
                  : f === 'pass' ? `Pass (${results.filter(r => r.passStatus === 'Pass').length})`
                  : f === 'fail' ? `Fail (${results.filter(r => r.passStatus === 'Fail').length})`
                  : `Needs Review (${results.filter(resultNeedsReview).length})`}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-linear-text-placeholder" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5L14 14" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              className="input input-sm pl-9"
              placeholder="Search dealer code or name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 ml-auto">
            {summary && summary.errors > 0 && (
              <button className="btn btn-outline" onClick={async () => {
                try {
                  const { resumeRTAudit } = await import('../api/index')
                  await resumeRTAudit(jobId!)
                  setError(null)
                  pollStatus(jobId!)
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed to resume audit')
                }
              }}>
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 8a6 6 0 0111.472-2.5M14 8a6 6 0 01-11.472 2.5" strokeLinecap="round" />
                  <path d="M14 2v4h-4M2 14v-4h4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Resume Failed ({summary?.errors ?? 0})
              </button>
            )}
            {(() => {
              const driveStatus = jobStatus?.driveUploadStatus
              const folderUrl = jobStatus?.driveRootFolderUrl
              const folderExists = jobStatus?.driveFolderExists

              const mediaIcon = (
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="2" width="12" height="10" rx="1.5" />
                  <path d="M2 9.5l3-2.5 2 1.5 3-3 4 3" />
                  <circle cx="10.5" cy="5" r="1.25" />
                </svg>
              )

              // Pre-implementation report (no driveUploadStatus) — offer to upload
              if (driveStatus === undefined) {
                return (
                  <span className="tooltip-wrapper" data-tooltip="Screenshots from this older report haven't been uploaded to Drive yet">
                    <button
                      className="btn btn-outline"
                      disabled={isRetryingUpload}
                      onClick={async () => {
                        setIsRetryingUpload(true)
                        try {
                          const { retryRTAuditDriveUpload } = await import('../api/index')
                          await retryRTAuditDriveUpload(jobId!)
                          pollStatus(jobId!)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to start drive upload')
                        } finally {
                          setIsRetryingUpload(false)
                        }
                      }}
                    >
                      {isRetryingUpload ? <span className="spinner spinner-sm" /> : (
                        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M3 10v3h10v-3M8 10V3M5 5l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      Upload to Drive
                    </button>
                  </span>
                )
              }

              // Upload in progress
              if (driveStatus === 'pending' || driveStatus === 'uploading') {
                return (
                  <span className="tooltip-wrapper" data-tooltip="Uploading media to Drive...">
                    <button className="btn btn-primary" disabled aria-label="View Media: uploading to Drive" style={{ pointerEvents: 'none' }}>
                      <span className="spinner spinner-sm" aria-hidden="true" />
                      View Media
                    </button>
                  </span>
                )
              }

              // Upload failed — show disabled View Media + Retry button
              if (driveStatus === 'failed') {
                return (
                  <>
                    <span className="tooltip-wrapper" data-tooltip="Media upload failed">
                      <button className="btn btn-primary" disabled aria-label="View Media: upload failed" style={{ pointerEvents: 'none' }}>
                        {mediaIcon}
                        View Media
                      </button>
                    </span>
                    <button
                      className="btn btn-outline"
                      disabled={isRetryingUpload}
                      onClick={async () => {
                        setIsRetryingUpload(true)
                        try {
                          const { retryRTAuditDriveUpload } = await import('../api/index')
                          await retryRTAuditDriveUpload(jobId!)
                          pollStatus(jobId!)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to retry drive upload')
                        } finally {
                          setIsRetryingUpload(false)
                        }
                      }}
                    >
                      {isRetryingUpload ? <span className="spinner spinner-sm" /> : (
                        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M2 8a6 6 0 0111.472-2.5M14 8a6 6 0 01-11.472 2.5" strokeLinecap="round" />
                          <path d="M14 2v4h-4M2 14v-4h4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      Retry Upload
                    </button>
                  </>
                )
              }

              // Upload completed
              if (driveStatus === 'completed') {
                // Folder was deleted — offer to re-upload from S3
                if (folderExists === false) {
                  return (
                    <span className="tooltip-wrapper" data-tooltip="Drive folder was deleted. Click to re-upload screenshots from S3.">
                      <button
                        className="btn btn-outline"
                        disabled={isRetryingUpload}
                        onClick={async () => {
                          setIsRetryingUpload(true)
                          try {
                            const { retryRTAuditDriveUpload } = await import('../api/index')
                            await retryRTAuditDriveUpload(jobId!)
                            pollStatus(jobId!)
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to start drive upload')
                          } finally {
                            setIsRetryingUpload(false)
                          }
                        }}
                      >
                        {isRetryingUpload ? <span className="spinner spinner-sm" /> : (
                          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 10v3h10v-3M8 10V3M5 5l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        Re-upload to Drive
                      </button>
                    </span>
                  )
                }

                // Folder exists or check inconclusive (null) — default to active
                const href = folderUrl || 'https://drive.google.com/drive/folders/1ue1F0YVL829sTHiI0zhhcpdqIQhKgGub'
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                    {mediaIcon}
                    View Media
                  </a>
                )
              }

              return null
            })()}
            <button className="btn btn-primary" onClick={handleExport} disabled={isExporting}>
              {isExporting ? <span className="spinner spinner-sm" /> : (
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 10v3h10v-3M8 3v7M5 8l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              Export XLSX
            </button>
          </div>
        </div>


        {/* Results table */}
        <div className="table-container overflow-x-auto">
          <table className="table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th className="cursor-pointer select-none" style={{ width: '100px' }} onClick={() => handleSort('dealerCode')}>
                  Dealer Code
                  <SortIcon active={sortField === 'dealerCode'} direction={sortDirection} />
                </th>
                <th className="cursor-pointer select-none" style={{ width: '180px' }} onClick={() => handleSort('dealerUrl')}>
                  Dealer URL
                  <SortIcon active={sortField === 'dealerUrl'} direction={sortDirection} />
                </th>
                {([
                  { key: 'c1_logo', label: 'C1', name: 'Logo', desc: 'Current Rent a Toyota logo on the rental page. The old green-swoosh "Toyota Rent a Car" mark fails. A pop-up logo that appears when the page loads still counts.' },
                  { key: 'c2_link', label: 'C2', name: 'Link', desc: 'Rent a Toyota link in the main nav or on the homepage. Generic "Rentals" or "loaners" wording no longer passes. Links buried inside a dropdown fail.' },
                  { key: 'c3_phone', label: 'C3', name: 'Phone', desc: 'Rental desk phone on the rental page or inside rental content. A "Phone:" line next to rental info counts. Numbers tagged for service or sales fail.' },
                  { key: 'c4_about', label: 'C4', name: 'About', desc: 'A rental About section on the rental page, more than one sentence about the rental program. FAQs about rentals count.' },
                  { key: 'c5_pricing', label: 'C5', name: 'Pricing', desc: 'Rental prices on the site with real dollar values. $0 still counts. "TBD" or "Call for pricing" fails.' },
                  { key: 'c6_reservations', label: 'C6', name: 'Reservations', desc: 'A reservation form for rentals that asks for a vehicle or date or something similar. Generic contact forms fail. It does not need to actually work.' },
                  { key: 'c7_displayed', label: 'C7', name: 'Displayed', desc: 'Toyota\'s PO tells us whether the dealer appears on toyota.com/rental. We do not audit this one.' },
                ] as { key: SortField; label: string; name: string; desc: string }[]).map((col, colIdx, arr) => {
                  const isRightEdge = colIdx >= arr.length - 2
                  return (
                  <th key={col.key} className="text-center whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort(col.key)}>
                    <span>{col.label}</span>
                    <SortIcon active={sortField === col.key} direction={sortDirection} />
                    <div className="relative inline-block ml-1 align-middle" data-criteria-tooltip>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenTooltip(openTooltip === col.key ? null : col.key) }}
                        className="w-4 h-4 rounded-full bg-linear-bg-hover hover:bg-linear-text-muted/20 text-linear-text-muted text-[10px] font-bold inline-flex items-center justify-center transition-colors"
                      >
                        i
                      </button>
                      {openTooltip === col.key && (
                        <div className={`absolute top-6 z-20 w-56 p-3 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg text-left normal-case whitespace-normal tracking-normal ${isRightEdge ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
                          <p className="text-xs font-semibold text-linear-text mb-1">{col.name}</p>
                          <p className="text-[11px] text-linear-text-secondary leading-relaxed font-normal">{col.desc}</p>
                        </div>
                      )}
                    </div>
                  </th>
                  )
                })}
                <th className="cursor-pointer select-none text-center" style={{ width: '80px' }} onClick={() => handleSort('subtotal')}>
                  Subtotal
                  <SortIcon active={sortField === 'subtotal'} direction={sortDirection} />
                  <div className="relative inline-block ml-1 align-middle" data-criteria-tooltip>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOpenTooltip(openTooltip === 'subtotal' ? null : 'subtotal') }}
                      className="w-4 h-4 rounded-full bg-linear-bg-hover hover:bg-linear-text-muted/20 text-linear-text-muted text-[10px] font-bold inline-flex items-center justify-center transition-colors"
                    >
                      i
                    </button>
                    {openTooltip === 'subtotal' && (
                      <div className="absolute right-0 top-6 z-20 w-56 p-3 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg text-left normal-case whitespace-normal tracking-normal">
                        <p className="text-xs font-semibold text-linear-text mb-1">Subtotal</p>
                        <p className="text-[11px] text-linear-text-secondary leading-relaxed font-normal">Each criterion is worth 2 points when met. Max is 14 points (or 12 if C7 is "No Audit").</p>
                      </div>
                    )}
                  </div>
                </th>
                <th className="cursor-pointer select-none text-center" style={{ width: '70px' }} onClick={() => handleSort('passStatus')}>
                  Status
                  <SortIcon active={sortField === 'passStatus'} direction={sortDirection} />
                  <div className="relative inline-block ml-1 align-middle" data-criteria-tooltip>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOpenTooltip(openTooltip === 'passStatus' ? null : 'passStatus') }}
                      className="w-4 h-4 rounded-full bg-linear-bg-hover hover:bg-linear-text-muted/20 text-linear-text-muted text-[10px] font-bold inline-flex items-center justify-center transition-colors"
                    >
                      i
                    </button>
                    {openTooltip === 'passStatus' && (
                      <div className="absolute right-0 top-6 z-20 w-56 p-3 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg text-left normal-case whitespace-normal tracking-normal">
                        <p className="text-xs font-semibold text-linear-text mb-1">Pass / Fail</p>
                        <p className="text-[11px] text-linear-text-secondary leading-relaxed font-normal">A dealer passes with 10+ points (at least 5 of 7 criteria met). Below 10 points is a fail.</p>
                      </div>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                // Loading skeleton while results are being fetched
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i}>
                    <td><div className="h-3 bg-linear-bg-hover rounded animate-pulse w-12" /></td>
                    <td><div className="h-3 bg-linear-bg-hover rounded animate-pulse w-32" /></td>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="text-center"><div className="h-3 bg-linear-bg-hover rounded animate-pulse w-5 mx-auto" /></td>
                    ))}
                    <td className="text-center"><div className="h-3 bg-linear-bg-hover rounded animate-pulse w-6 mx-auto" /></td>
                    <td className="text-center"><div className="h-5 bg-linear-bg-hover rounded-full animate-pulse w-10 mx-auto" /></td>
                  </tr>
                ))
              ) : filteredResults.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-10 text-linear-text-muted">
                    No results match your filters.
                  </td>
                </tr>
              ) : (
                filteredResults.map(result => (
                  <React.Fragment key={result.dealerCode}>
                    <tr
                      className="cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === result.dealerCode ? null : result.dealerCode)}
                    >
                      <td className="font-mono text-[12px]">
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${result.cached ? 'bg-blue-400' : 'bg-gray-500'}`}
                          title={result.cached
                            ? (result.capturedAt
                                ? `Served from cache (captured ${new Date(result.capturedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })})`
                                : 'Served from cache')
                            : 'Captured fresh this run'}
                        />
                        {result.dealerCode}
                      </td>
                      <td>
                        <div className="max-w-[260px] text-[12px] space-y-0.5">
                          <div className="truncate">
                            {result.dealerUrl && /^https?:\/\//i.test(result.dealerUrl) ? (
                              <a
                                href={result.dealerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-linear-accent hover:underline"
                                onClick={e => e.stopPropagation()}
                              >
                                {result.dealerUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')}
                              </a>
                            ) : (
                              <span className="text-linear-text-placeholder">{result.dealerUrl || '-'}</span>
                            )}
                          </div>
                          {/* Phase 8 — surface rental page URL on the row.
                              Previously buried in the expand panel; reviewers
                              had to click every row to compare URLs against
                              the manual CSV. Showing it here makes URL drift
                              visible at scan time. */}
                          {(() => {
                            const rentalUrl = result.rentalPageUrls?.[0] ?? result.rentalPageUrl
                            if (!rentalUrl) return null
                            return (
                              <div className="truncate text-linear-text-muted text-[11px]">
                                <span className="text-linear-text-placeholder">rental:</span>{' '}
                                {/^https?:\/\//i.test(rentalUrl) ? (
                                  <a
                                    href={rentalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    {rentalUrl.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                                  </a>
                                ) : (
                                  rentalUrl
                                )}
                              </div>
                            )
                          })()}
                        </div>
                      </td>
                      <td className="text-center"><CriterionCell value={result.c1_logo} /></td>
                      <td className="text-center"><CriterionCell value={result.c2_link} /></td>
                      <td className="text-center"><CriterionCell value={result.c3_phone} /></td>
                      <td className="text-center"><CriterionCell value={result.c4_about} /></td>
                      <td className="text-center"><CriterionCell value={result.c5_pricing} /></td>
                      <td className="text-center"><CriterionCell value={result.c6_reservations} /></td>
                      <td className="text-center"><CriterionCell value={result.c7_displayed} /></td>
                      <td className="text-center font-medium text-linear-text">{result.subtotal}</td>
                      <td className="text-center">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`badge ${result.passStatus === 'Pass' ? 'badge-success' : 'badge-error'}`}>
                            {result.passStatus}
                          </span>
                          {resultNeedsReview(result) && (
                            <svg
                              className="w-3.5 h-3.5 text-yellow-500 shrink-0"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              role="img"
                              aria-label="Needs review"
                            >
                              <title>{result.flags?.length ? result.flags.join(' · ') : 'Needs review'}</title>
                              <path d="M8 1.5L15 14H1L8 1.5z" opacity="0.15" />
                              <path d="M8 1.5L15 14H1L8 1.5z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                              <path d="M8 6v3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                              <circle cx="8" cy="11.8" r="0.8" />
                            </svg>
                          )}
                        </span>
                      </td>
                    </tr>
                    {expandedRow === result.dealerCode && (
                      <tr>
                        <td colSpan={11} className="!p-0">
                          <div className="bg-linear-bg-card px-6 py-4 border-t border-linear-border animate-fade-in">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h5 className="text-[12px] font-medium text-linear-text-muted uppercase tracking-wider mb-2">Details</h5>
                                <div className="space-y-1 text-[13px]">
                                  <p><span className="text-linear-text-muted">Dealer URL:</span>{' '}
                                    {result.dealerUrl && /^https?:\/\//i.test(result.dealerUrl) ? (
                                      <a href={result.dealerUrl} target="_blank" rel="noopener noreferrer" className="text-linear-accent hover:underline">{result.dealerUrl}</a>
                                    ) : result.dealerUrl || '-'}
                                  </p>
                                  {(() => {
                                    const rentalUrls = result.rentalPageUrls?.length
                                      ? result.rentalPageUrls
                                      : result.rentalPageUrl ? [result.rentalPageUrl] : []
                                    if (rentalUrls.length === 0) {
                                      return <p><span className="text-linear-text-muted">Rental Page:</span> <span className="text-linear-text-placeholder">Not found</span></p>
                                    }
                                    if (rentalUrls.length === 1) {
                                      return (
                                        <p><span className="text-linear-text-muted">Rental Page:</span>{' '}
                                          {/^https?:\/\//i.test(rentalUrls[0]) ? (
                                            <a href={rentalUrls[0]} target="_blank" rel="noopener noreferrer" className="text-linear-accent hover:underline">{rentalUrls[0]}</a>
                                          ) : rentalUrls[0]}
                                        </p>
                                      )
                                    }
                                    return (
                                      <div>
                                        <span className="text-linear-text-muted">Rental Pages:</span>
                                        {rentalUrls.map((url, idx) => (
                                          <p key={idx} className="ml-2">
                                            <span className="text-linear-text-muted">{idx + 1}.</span>{' '}
                                            {/^https?:\/\//i.test(url) ? (
                                              <a href={url} target="_blank" rel="noopener noreferrer" className="text-linear-accent hover:underline">{url}</a>
                                            ) : url}
                                          </p>
                                        ))}
                                      </div>
                                    )
                                  })()}
                                  <p><span className="text-linear-text-muted">Total Points:</span> <span className="text-linear-text">{result.totalPoints}</span></p>
                                  <p><span className="text-linear-text-muted">Confidence:</span> <span className="text-linear-text">{result.confidence || '-'}</span></p>
                                  <p>
                                    <span className="text-linear-text-muted">Cached on:</span>{' '}
                                    <span className="text-linear-text">
                                      {result.cached && result.capturedAt
                                        ? new Date(result.capturedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
                                        : 'N/A'}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div>
                                {result.notes && (
                                  <div className="mb-3">
                                    <h5 className="text-[12px] font-medium text-linear-text-muted uppercase tracking-wider mb-2">Notes</h5>
                                    {/* Notes are one sentence per line now (server joins on \n) */}
                                    <p className="text-[13px] text-linear-text-secondary whitespace-pre-line">{result.notes}</p>
                                  </div>
                                )}
                                {result.flags && result.flags.length > 0 && (
                                  <div className="mb-3">
                                    <h5 className="text-[12px] font-medium text-linear-text-muted uppercase tracking-wider mb-2">Flags</h5>
                                    <div className="flex flex-wrap gap-1">
                                      {result.flags.map((flag, i) => (
                                        <span key={i} className="badge badge-warning">{flag}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {result.screenshotUrls && result.screenshotUrls.length > 0 && (
                                  <div>
                                    <h5 className="text-[12px] font-medium text-linear-text-muted uppercase tracking-wider mb-2">Screenshots</h5>
                                    <div className="flex flex-wrap gap-2">
                                      {result.screenshotUrls.map((url, i) => {
                                        const label = result.screenshotKeys?.[i]?.split('/').pop()?.replace('.jpeg', '').replace('.png', '') || `Screenshot ${i + 1}`
                                        const allImages = result.screenshotUrls!.map((u, idx) => ({
                                          url: u,
                                          dealerCode: result.dealerCode,
                                          label: result.screenshotKeys?.[idx]?.split('/').pop()?.replace('.jpeg', '').replace('.png', '') || `Screenshot ${idx + 1}`,
                                        }))
                                        return (
                                          <button
                                            key={i}
                                            onClick={() => openImageModal(url, result.dealerCode, label, allImages)}
                                            className="w-28 h-20 bg-linear-bg-hover border border-linear-border-input rounded overflow-hidden cursor-zoom-in relative group"
                                            title={`Click to view: ${label}`}
                                          >
                                            <img
                                              src={url}
                                              alt={`${result.dealerCode} ${label}`}
                                              className="w-full h-full object-cover object-top"
                                              onError={(e) => {
                                                const img = e.currentTarget
                                                img.style.display = 'none'
                                                const placeholder = document.createElement('div')
                                                placeholder.className = 'w-full h-full flex items-center justify-center text-[10px] text-linear-text-muted'
                                                placeholder.textContent = 'No image'
                                                img.parentElement?.insertBefore(placeholder, img)
                                              }}
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all">
                                              <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                              </svg>
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-[10px] text-linear-text-muted truncate">
                                              {label}
                                            </div>
                                          </button>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Image Lightbox Modal */}
        {imageModal.isOpen && imageModal.images.length > 0 && (() => {
          const currentImage = imageModal.images[imageModal.currentIndex]
          const canGoPrev = imageModal.currentIndex > 0
          const canGoNext = imageModal.currentIndex < imageModal.images.length - 1
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
              onClick={closeImageModal}
            >
              <div
                className="relative w-full h-full flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">
                        Dealer {currentImage.dealerCode}
                      </h3>
                      <span className="text-sm text-linear-text-muted">{currentImage.label}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      {imageModal.images.length > 1 && (
                        <span className="text-sm">
                          {imageModal.currentIndex + 1} / {imageModal.images.length}
                        </span>
                      )}
                      <button
                        onClick={closeImageModal}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                        title="Close (ESC)"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main image */}
                <div className="flex-1 overflow-auto p-4 pt-20 pb-24">
                  <div className="min-h-full flex items-center justify-center">
                    <img
                      src={currentImage.url}
                      alt={`${currentImage.dealerCode} ${currentImage.label}`}
                      className="max-w-none h-auto"
                      style={{
                        transform: `scale(${imageModal.zoomLevel})`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        e.currentTarget.insertAdjacentHTML('afterend',
                          '<div class="flex flex-col items-center justify-center text-linear-text-muted py-20"><svg class="w-12 h-12 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><span class="text-sm">Screenshot not available</span></div>'
                        )
                      }}
                    />
                  </div>
                </div>

                {/* Bottom controls */}
                <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black via-black/90 to-transparent p-6 pb-4">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-4 text-sm">
                      <span>
                        <span className="text-linear-text-muted">Dealer:</span> {currentImage.dealerCode}
                      </span>
                      <span>
                        <span className="text-linear-text-muted">Page:</span> {currentImage.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleZoom(-0.25)}
                        disabled={imageModal.zoomLevel <= 0.5}
                        className="p-2 hover:bg-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Zoom out (-)"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                        </svg>
                      </button>
                      <span className="text-sm w-16 text-center">{Math.round(imageModal.zoomLevel * 100)}%</span>
                      <button
                        onClick={() => handleZoom(0.25)}
                        disabled={imageModal.zoomLevel >= 3}
                        className="p-2 hover:bg-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Zoom in (+)"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setImageModal((prev) => ({ ...prev, zoomLevel: 1 }))}
                        className="p-2 hover:bg-white/20 rounded transition-colors text-xs"
                        title="Reset zoom"
                      >
                        Reset
                      </button>
                      <div className="w-px h-6 bg-white/30 mx-1" />
                      <a
                        href={currentImage.url}
                        download={`${currentImage.dealerCode}_${currentImage.label}.jpeg`}
                        className="flex items-center gap-1 px-3 py-2 hover:bg-white/20 rounded transition-colors text-sm"
                        title="Download image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span>Download</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Navigation arrows */}
                {imageModal.images.length > 1 && (
                  <>
                    {canGoPrev && (
                      <button
                        onClick={() => navigateImage('prev')}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors text-white"
                        title="Previous (left arrow)"
                      >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    {canGoNext && (
                      <button
                        onClick={() => navigateImage('next')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors text-white"
                        title="Next (right arrow)"
                      >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        })()}

        {renderOverlays()}
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render: Loading state (jobId present but status not yet fetched)
  // -----------------------------------------------------------------------

  if (jobId && !jobStatus) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="spinner spinner-lg" />
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render: Upload CSV (default state)
  // -----------------------------------------------------------------------

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[20px] font-medium text-linear-text">RT Audit</h1>
        <p className="text-[13px] text-linear-text-muted mt-1">
          Quarterly dealer website audit for the Rent a Toyota program
        </p>
      </div>

      {/* Upload zone */}
      <div
        className={`upload-zone ${isDragOver ? 'drag-active' : ''} mb-6`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Upload CSV file"
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click() }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileInputChange}
        />

        {csvFile ? (
          <div>
            <svg className="w-10 h-10 mx-auto mb-3 text-success" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="8" y="4" width="24" height="32" rx="3" />
              <path d="M14 20l4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="text-[15px] font-medium text-linear-text mb-1">{csvFile.name}</p>
            <p className="text-[13px] text-linear-text-muted">
              {parsedCSV ? `${parsedCSV.rows.length} dealer rows parsed` : 'Parsing...'}
            </p>
            <button
              className="text-[12px] text-linear-accent hover:text-linear-accent-focus mt-2 transition-colors"
              onClick={(e) => { e.stopPropagation(); setCsvFile(null); setParsedCSV(null) }}
            >
              Remove and upload different file
            </button>
          </div>
        ) : (
          <div>
            <svg className="w-10 h-10 mx-auto mb-3 text-linear-text-placeholder" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="8" y="4" width="24" height="32" rx="3" />
              <path d="M16 20h8M20 16v8" strokeLinecap="round" />
            </svg>
            <p className="text-[15px] font-medium text-linear-text mb-1">
              Drop CSV file here or click to upload
            </p>
            <p className="text-[13px] text-linear-text-muted">
              Accepts .csv files with dealer code and URL columns
            </p>
          </div>
        )}
      </div>

      {/* Validation warnings */}
      {parsedCSV && parsedCSV.warnings.length > 0 && (
        <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 4v4M8 10v1M3 14h10L8 3 3 14z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <p className="text-[13px] font-medium text-warning mb-1">Validation Warnings</p>
              <ul className="text-[12px] text-warning/80 space-y-0.5">
                {parsedCSV.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* CSV Summary card */}
      {csvSummary && csvSummary.total > 0 && (
        <div className="card mb-6">
          <h3 className="text-[13px] font-medium text-linear-text-muted uppercase tracking-wider mb-4">CSV Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-[24px] font-bold text-linear-text">{csvSummary.total}</div>
              <p className="text-[12px] text-linear-text-muted">Total Dealers</p>
            </div>
            <div>
              <div className="text-[24px] font-bold text-success">{csvSummary.c7Y}</div>
              <p className="text-[12px] text-linear-text-muted">C7 = Y</p>
            </div>
            <div>
              <div className="text-[24px] font-bold text-error">{csvSummary.c7N}</div>
              <p className="text-[12px] text-linear-text-muted">C7 = N</p>
            </div>
            <div>
              <div className="text-[24px] font-bold text-linear-text-placeholder">{csvSummary.c7NoAudit}</div>
              <p className="text-[12px] text-linear-text-muted">C7 = No Audit</p>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-error flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="8" cy="8" r="6" />
              <path d="M8 5v3M8 10v1" strokeLinecap="round" />
            </svg>
            <p className="text-[13px] text-error">{error}</p>
          </div>
        </div>
      )}

      {/* Pre-run cache callout */}
      {cacheStatus && cacheStatus.cachedCount > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-linear-border bg-linear-bg-hover/40 p-3 text-[12px] text-linear-text-muted">
          <svg className="mt-0.5 w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 4v4l2.5 1.5M8 14A6 6 0 108 2a6 6 0 000 12z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>
            <span className="text-linear-text font-medium">{cacheStatus.cachedCount} of {cacheStatus.total}</span> dealer{cacheStatus.total === 1 ? '' : 's'} have a cached capture from the last {cacheStatus.ttlDays} days and will be reused (no re-capture). The remaining {cacheStatus.total - cacheStatus.cachedCount} will be captured fresh. To re-capture everything, hit the Clear Cache button below before starting.
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary btn-lg"
          onClick={() => setShowStartConfirm(true)}
          disabled={!csvFile || !parsedCSV || parsedCSV.rows.length === 0 || isStarting}
        >
          {isStarting ? (
            <>
              <span className="spinner spinner-sm" />
              Starting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 3l9 5-9 5V3z" fill="currentColor" stroke="none" />
              </svg>
              Start Audit
            </>
          )}
        </button>

        <button
          className="btn btn-outline"
          onClick={handleDownloadExample}
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M8 2v8M5 7l3 3 3-3M3 12h10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Download CSV Template
        </button>

        {globalCachedCount !== null && globalCachedCount > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-linear-text-muted">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
            {globalCachedCount} dealer{globalCachedCount === 1 ? '' : 's'} cached
          </span>
        )}
        <button
          className={`btn btn-ghost ${globalCachedCount !== null && globalCachedCount > 0 ? '' : 'ml-auto'}`}
          onClick={() => { setClearCacheMsg(null); setShowClearCache(true) }}
          disabled={isClearingCache || globalCachedCount === 0}
          data-tooltip={globalCachedCount === 0 ? 'Nothing is cached yet' : 'Clear all cached captures so the next run re-captures every dealer fresh'}
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 4h12M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l.5 9a1 1 0 001 1h3a1 1 0 001-1L11 4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Clear Cache
        </button>
      </div>

      {clearCacheMsg && (
        <p className="mt-3 text-[12px] text-linear-text-muted">{clearCacheMsg}</p>
      )}

      {showStartConfirm && parsedCSV && (
        <ConfirmModal
          title="Start this audit?"
          message={`This run covers ${parsedCSV.rows.length} dealer${parsedCSV.rows.length === 1 ? '' : 's'}. Audits cost money and take a while to finish, so make sure this is the file you want.`}
          confirmLabel="Start Audit"
          onConfirm={() => {
            setShowStartConfirm(false)
            handleStartAudit()
          }}
          onCancel={() => setShowStartConfirm(false)}
        />
      )}

      {showClearCache && (
        <ConfirmModal
          title="Clear capture cache?"
          message="This deletes every cached capture (screenshots + verdicts) for all dealers. The next run will re-capture each dealer fresh, which is slower and uses more Bright Data. This does not affect any past audit results or their Drive folders. Continue?"
          confirmLabel={isClearingCache ? 'Clearing…' : 'Clear cache'}
          onConfirm={handleClearCache}
          onCancel={() => setShowClearCache(false)}
          isLoading={isClearingCache}
          danger
        />
      )}

      {renderOverlays()}
    </div>
  )
}

// ============================================================================
// Page-level component with routing
// ============================================================================

export default function RTAudit() {
  const location = useLocation()
  const [currentView, setCurrentView] = useState<'home' | 'processing' | 'results' | 'error'>('home')
  const [headerActions, setHeaderActions] = useState<ReactNode>(null)

  // Instructions page — rendered OUTSIDE AppLayout to avoid double nesting
  // (RTAuditInstructions wraps itself in its own AppLayout with breadcrumbs)
  if (location.pathname.includes('/instructions')) {
    return (
      <Suspense fallback={
        <div className="flex items-center justify-center py-20">
          <span className="spinner spinner-lg" />
        </div>
      }>
        <RTAuditInstructions />
      </Suspense>
    )
  }

  // Breadcrumbs adapt to current view
  const breadcrumbLabel =
    currentView === 'processing' ? 'Processing' :
    currentView === 'results' ? 'Results' :
    currentView === 'error' ? 'Error' :
    null

  const breadcrumbs = breadcrumbLabel
    ? [
        { label: 'RT Audit', href: '/rt-audit/' },
        { label: breadcrumbLabel },
      ]
    : [
        { label: 'RT Audit' },
      ]

  return (
    <AppLayout breadcrumbs={breadcrumbs} headerActions={headerActions}>
      <RTAuditMain onViewChange={setCurrentView} onHeaderActionsChange={setHeaderActions} />
    </AppLayout>
  )
}
